import { useReportsImport } from '@/api/WhisApi/generated/reports/reports';
import { showToast } from '@/components/Toast/toast.service';
import type { UploadedDocument } from '@/types/formField.types';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import {
  buildImportReportDto,
  mapAndValidateReportCsv,
} from '@screens/ReportCreation/components/CsvImport/utils/csvImport.utils';
import { ValidatedCsvReportResult } from '@screens/ReportCreation/components/CsvImport/types/csvValidation.types';
import type { ReportSubmissionMode } from '@screens/ReportCreation/reportCreation.types';
import type { ReferentialsResponseDto } from '@api/WhisApi/generated';

interface UseCsvImportValidationResult {
  isValidating: boolean;
  validationResult: ValidatedCsvReportResult | null;
  csvContent: string | null;
  fileValidationErrorMessage?: string;
  validateCsvFile: (file: UploadedDocument | undefined) => Promise<void>;
  resetValidation: () => void;
  publishCsv: () => void;
  sendCsvAsDraft: () => void;
  isImporting: boolean;
}

const readFileContent = async (uri: string): Promise<string> => {
  const response = await fetch(uri);
  if (!response.ok) {
    throw new Error('Unable to read selected file');
  }
  return response.text();
};

export const useCsvImport = (
  referentials?: ReferentialsResponseDto,
): UseCsvImportValidationResult => {
  const { t } = useAppTranslation();
  const router = useRouter();
  const { mutate: importReports, isPending: isImporting } = useReportsImport();
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] =
    useState<ValidatedCsvReportResult | null>(null);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [fileValidationErrorMessage, setFileValidationErrorMessage] = useState<
    string | undefined
  >();

  const validateCsvFile = useCallback(
    async (file: UploadedDocument | undefined): Promise<void> => {
      if (!file) {
        setValidationResult(null);
        setCsvContent(null);
        setFileValidationErrorMessage(
          t('reportCreation.csvImportValidation.errors.noFileSelected'),
        );
        return;
      }

      setIsValidating(true);
      setFileValidationErrorMessage(undefined);

      try {
        const csvContent = await readFileContent(file.uri);
        const result = mapAndValidateReportCsv(csvContent, t, referentials);

        setCsvContent(csvContent);
        setValidationResult(result);
      } catch (error) {
        console.error('CSV validation failed', error);
        setCsvContent(null);
        setValidationResult(null);
        setFileValidationErrorMessage(
          t('reportCreation.csvImportValidation.errors.unableToReadFile'),
        );
      } finally {
        setIsValidating(false);
      }
    },
    [t, referentials],
  );

  const submitValidatedCsvImport = useCallback(
    (status: ReportSubmissionMode): void => {
      if (isImporting || !validationResult) {
        return;
      }

      // Build reports directly from parsed rows
      const reports = validationResult.parsedRows.map((r) =>
        buildImportReportDto(r, status),
      );

      importReports(
        {
          data: {
            reports,
          },
        },
        {
          onSuccess: (result) => {
            router.replace(
              `/report-creation/import-success?published=${result.published}&draft=${result.draft}&error=${result.error}`,
            );
          },
          onError: () => {
            showToast({
              type: 'error',
              title: t('reportCreation.toasts.submitError'),
            });
          },
        },
      );
    },
    [importReports, router, t, validationResult, isImporting],
  );

  const publishCsv = (): void => submitValidatedCsvImport('published');
  const sendCsvAsDraft = (): void => submitValidatedCsvImport('draft');

  const resetValidation = useCallback((): void => {
    setCsvContent(null);
    setValidationResult(null);
    setFileValidationErrorMessage(undefined);
  }, []);

  return {
    isValidating,
    validationResult,
    csvContent,
    fileValidationErrorMessage,
    validateCsvFile,
    resetValidation,
    publishCsv,
    sendCsvAsDraft,
    isImporting,
  };
};
